import React, { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../helpers/axiosInstance";
import Swal from "sweetalert2";
import Edit from "../images/edit.png";
import Delete from "../images/delete.svg";
import View from "../images/view.svg";
import { jwtDecode } from "jwt-decode";
import { FaFilter } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import { useLoading } from '../context/LoadingContext';
import { BiRefresh } from "react-icons/bi";
import { FiSave } from "react-icons/fi";

const Device = () => {
    const [devices, setDevices] = useState([]);
    const [editDevice, setEditDevice] = useState(null);
    const [metadataForm, setMetadataForm] = useState({});
    const [status, setStatus] = useState("");
    const [locationOptions, setLocationOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [inputPage, setInputPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();
    const [showTypeFilter, setShowTypeFilter] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const { updateLoading } = useLoading();

    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const limit = 10; // Number of items per page

    const typeOptions = ["ALPR", "RFID", "Boom Barrier", "Display", "Ultrasonic Sensor"];
    const statusOptions = ["Active", "Inactive", "Maintenance", "Removed", "Installed"];
    const EditStatusOptions = ["Active", "Inactive", "Maintenance"];



    const handleView = (id) => {
        navigate(`/device/${id}`);
    };


    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const fetchDevices = useCallback(async () => {
        updateLoading(true);
        try {
            const params = {
                page: currentPage,
                limit,
            };

            if (selectedStatus) params.status = selectedStatus;
            if (selectedType) params.type = selectedType;
            if (searchTerm) params.search = searchTerm;

            const response = await axiosInstance.get("/api/device/devices", { params });
            const total = response.data.totalDevices || 0;
            const totalPagesCalculated = Math.ceil(total / limit) || 1;

            // Always update devices
            setDevices(response.data.devices || []);
            setInputPage(currentPage);
            setTotalPages(totalPagesCalculated);

            // Adjust currentPage if it exceeds totalPages
            if (currentPage > totalPagesCalculated) {
                setCurrentPage(totalPagesCalculated || 1);
            }
        } catch (error) {
            setDevices([]);
            setTotalPages(1);
            setInputPage(1);
        } finally {
            updateLoading(false);
        }
    }, [currentPage, selectedStatus, selectedType, limit, searchTerm, updateLoading]);
    // Add dependencies here

    useEffect(() => {
        fetchDevices();
    }, [fetchDevices]);

    const handleEdit = async (device) => {
        try {
            const response = await axiosInstance.get(`/api/device/devices/${device.id}`);
            const deviceData = response.data;

            const initialMetadataForm = {};
            deviceData.Device_Metadata.forEach(metadata => {
                initialMetadataForm[metadata.key_name] = metadata.value_name;
            });

            setMetadataForm(initialMetadataForm);
            setStatus(deviceData.status);
            setEditDevice(deviceData);
        } catch (error) {
            console.error("Error fetching device details:", error);
        }
    };

    const handleMetadataChange = (key, value) => {
        setMetadataForm(prev => ({ ...prev, [key]: value }));
    };

    const handleMetadataSubmit = async () => {
        try {
            const updatedMetadata = Object.entries(metadataForm).map(([key_name, value_name]) => ({
                key_name,
                value_name
            }));
            await axiosInstance.put(`/api/device/devices/${editDevice.id}/metadata`, { metadata: updatedMetadata });
            Swal.fire("Success", "Metadata updated successfully!", "success");
            setEditDevice(null);
            fetchDevices();
        } catch (error) {
            console.error("Error updating metadata:", error);

            const backendMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to update metadata.";

            Swal.fire("Error", backendMessage, "error");
        }

    };

    const handleStatusSubmit = async () => {
        try {
            if (!status) {
                Swal.fire("Error", "Please select a status.", "error");
                return;
            }

            await axiosInstance.put(`/api/device/devices/${editDevice.id}/status`, { status });
            Swal.fire("Success", "Status updated successfully!", "success");
            setEditDevice(null);
            fetchDevices();
        } catch (error) {
            console.error("Error updating status:", error);

            const backendMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to update status.";

            Swal.fire("Error", backendMessage, "error");
        }
    };





    const handleDelete = async (id) => {
        const confirmDelete = await Swal.fire({
            title: "Are you sure?",
            text: "This device will be permanently removed!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove it!"
        });

        if (confirmDelete.isConfirmed) {
            let removedBy = null;
            const token = localStorage.getItem("authToken");

            if (token) {
                try {
                    const decodedToken = jwtDecode(token);
                    removedBy = decodedToken.userId || null;
                } catch (error) {
                    console.error("Error decoding token:", error);
                }
            }

            try {
                await axiosInstance.delete(`/api/device/devices/${id}`, {
                    data: { removed_by: removedBy }
                });

                Swal.fire("Removed!", "The device has been removed.", "success");
                fetchDevices();
            } catch (error) {
                console.error("Error removing device:", error);

                const backendMessage =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to remove the device.";

                Swal.fire("Error!", backendMessage, "error");
            }

        }
    };

    const handleReinstall = async (id, type) => {
        console.log("Reinstalling device with ID:", id, "and type:", type);
        if (!id || !type) return;
        const reinstallableTypes = ["ALPR", "RFID", "Display", "Boom Barrier", "Ultrasonic Sensor"];

        if (!reinstallableTypes.includes(type)) {
            Swal.fire("Error", `${type} device cannot be reinstalled.`, "error");
            return;
        }
        if (type === "Ultrasonic Sensor") {
            try {
                const response = await axiosInstance.get("/api/bays/active-empty-bay");
                const baysArray = response.data.data.bays;

                if (!baysArray.length) {
                    return Swal.fire("No Bays", "No available bays found.", "info");
                }

                // Convert to dropdown options
                const locations = baysArray.map((bay) => ({
                    value: bay.id,
                    label: `${bay.number} (${bay.shop?.shop_name || "No Shop"})`
                }));

                setLocationOptions(locations); // Update state for dropdown

                // Show selection modal
                const { value: selectedBayId } = await Swal.fire({
                    title: "Select a Bay",
                    html: "Select a bay for reinstallation.",
                    input: "select",
                    inputOptions: Object.fromEntries(locations.map(({ value, label }) => [value, label])),
                    inputPlaceholder: "Choose a bay",
                    showCancelButton: true,
                    inputValidator: (value) => {
                        if (!value) return "Please select a bay!";
                    }
                });

                if (selectedBayId) {
                    const selectedBayLabel = locations.find(bay => bay.value === selectedBayId)?.label || "Unknown Bay";
                    const confirmReinstall = await Swal.fire({
                        title: "Are you sure?",
                        text: `This device will be reinstalled to ${selectedBayLabel}!`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#3085d6",
                        confirmButtonText: "Yes, Reinstall it!"
                    });

                    if (confirmReinstall.isConfirmed) {
                        let installedBy = null;
                        const token = localStorage.getItem("authToken");

                        if (token) {
                            try {
                                const decoded = jwtDecode(token);
                                installedBy = decoded.userId;
                            } catch (err) {
                                console.error("Token decode error:", err);
                            }
                        }
                        try {
                            await axiosInstance.post(`/api/device/devices/${id}/reinstall`, {
                                type,
                                installed_by: installedBy,
                                bay_id: selectedBayId // Pass the selected bay
                            });
                            fetchDevices();
                        }
                        catch (err) {
                            console.error("Error reinstalling device:", err.response?.data || err);
                            Swal.fire("Error!", err.response?.data?.message || "Failed to reinstall the device.", "error");
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching bays:", error);
                Swal.fire("Error", "Failed to fetch bays.", "error");
            }
        } else {
            try {
                console.log(`Fetching locations for ${type}`);
                const response = await axiosInstance.get(`/api/plant/locationsWithoutDevices/${type}`);
                const locationsArray = response.data.data;
                if (!locationsArray.length) {
                    return Swal.fire("No Locations", "No available locations found.", "info");
                }
                const locationOptions = locationsArray.map((loc) => ({
                    value: loc.id,
                    label: `${loc.location_name} (${loc.module.module_name})` // Format: Gate-2 (Entry)
                }));
                setLocationOptions(locationOptions);
                const { value } = await Swal.fire({
                    title: "Select a Location",
                    html: "Select a location for reinstallation.",
                    input: "select",
                    inputOptions: Object.fromEntries(locationOptions.map(({ value, label }) => [value, label])),
                    inputPlaceholder: "Choose a location",
                    showCancelButton: true,
                    inputValidator: (value) => {
                        if (!value) return "Please select a location!";
                    }
                });
                if (value) {
                    const selectedLocationLabel = locationOptions.find(loc => loc.value === value)?.label || "Unknown Location";
                    const confirmReinstall = await Swal.fire({
                        title: "Are you sure?",
                        text: `This device will be reinstalled to ${selectedLocationLabel}!`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#3085d6",
                        confirmButtonText: "Yes, Reinstall it!"
                    });

                    if (confirmReinstall.isConfirmed) {
                        let installedBy = null;
                        const token = localStorage.getItem("authToken");

                        if (token) {
                            try {
                                const decoded = jwtDecode(token);
                                installedBy = decoded.userId;
                            } catch (err) {
                                console.error("Token decode error:", err);
                            }
                        }

                        try {
                            await axiosInstance.post(`/api/device/devices/${id}/reinstall`, {
                                type,
                                installed_by: installedBy,
                                location_id: value // Pass the selected location ID
                            });
                            fetchDevices(); // Refresh list
                        } catch (err) {
                            console.error("Error reinstalling device:", err.response?.data || err);
                            Swal.fire("Error!", err.response?.data?.message || "Failed to reinstall the device.", "error");
                        }
                    }
                }




            }
            catch (err) {
                console.error("Error fetching locations:", err);
                Swal.fire("Error", "Failed to fetch locations.", "error");
            }
        }

    }

    const handleInputChange = (e) => {
        setInputPage(e.target.value);
    };

    const handleInputSubmit = () => {
        let pageNumber = Number(inputPage);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        } else {
            setInputPage(currentPage); // reset if invalid
        }
    };

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2 fw-bold">
                <input
                    type="text"
                    className="form-control w-25"
                    placeholder="Search by Name or Type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <NavLink
                    to="/addDevice"
                    className="btn text-white fw-bold "
                    style={{ backgroundColor: "#0A4595", letterSpacing: "1px" }}
                >
                    ADD DEVICE
                </NavLink>
            </div>

            <div className="table-responsive mx-4 mt-4">
                <table className="table table-sm">
                    <thead className="text-white">
                        <tr>
                            <th className="text-center py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "200px", color: 'white' }}>Name</th>

                            {/* Type Column with Filter */}
                            <th className="text-center py-2 px-0 position-relative" style={{ backgroundColor: "#0A4595", minWidth: "200px", color: 'white' }}>
                                Type
                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowTypeFilter(!showTypeFilter)} />
                                <br />
                                {showTypeFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed" }}
                                    >
                                        {typeOptions.map((type) => (
                                            <div
                                                key={type}
                                                className="dropdown-item rounded px-3 py-2 text-dark fw-semibold"
                                                onClick={() => {
                                                    setSelectedType(type);
                                                    setShowTypeFilter(false);
                                                    setCurrentPage(1); // Reset to first page when filter changes
                                                    // fetchDevices will be called automatically due to the useEffect
                                                }}
                                                style={{
                                                    cursor: "pointer",
                                                    transition: "background-color 0.2s",
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                                {type}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedType && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedType}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px", }} onClick={() => setSelectedType("")} />
                                    </div>
                                )}

                            </th>
                            <th className="text-center py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "200px", color: 'white' }}>Last Installed At</th>

                            {/* Status Column with Filter */}
                            <th className="text-center py-2 px-0 position-relative" style={{ backgroundColor: "#0A4595", minWidth: "200px", color: 'white' }}>
                                Status

                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowStatusFilter(!showStatusFilter)} />
                                <br />
                                {showStatusFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed" }}
                                    >
                                        {statusOptions.map((status) => (
                                            <div
                                                key={status}
                                                className="dropdown-item rounded px-3 py-2 text-dark fw-semibold hover-bg"
                                                onClick={() => {
                                                    setSelectedStatus(status.toLowerCase());
                                                    setShowStatusFilter(false);
                                                    setCurrentPage(1); // Reset to first page when filter changes
                                                    // fetchDevices will be called automatically due to the useEffect
                                                }}
                                                style={{
                                                    cursor: "pointer",
                                                    transition: "background-color 0.2s",
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                            >
                                                {status}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {selectedStatus && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedStatus}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px" }} onClick={() => setSelectedStatus("")} />
                                    </div>
                                )}
                            </th>


                            <th className="text-center py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "150px", color: 'white' }}>Actions</th>
                        </tr>
                    </thead>


                    <tbody>
                        {devices.length > 0 ? (
                            devices.map((device, index) => (
                                <tr key={device.id}>

                                    <td className="text-center p-2">{device.name}</td>
                                    <td className="text-center p-2">{device.type}</td>
                                    <td className="text-center p-2">{device.installationLocation}</td>
                                    <td className="text-center p-2">{device.status}</td>
                                    <td className="text-center p-2">
                                        <img src={View} alt="View" className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: 'pointer' }} title="View" onClick={(e) => {
                                            handleView(device.id)
                                        }} />
                                        <img src={Edit} alt="Edit" className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: 'pointer' }} title="Edit" onClick={(e) => {
                                            handleEdit(device)
                                        }} />
                                        {
                                            device.status === "removed" ? (
                                                <BiRefresh
                                                    className="mx-2  text-success"
                                                    style={{ cursor: "pointer" }}
                                                    size={20}
                                                    title="Reinstall"
                                                    onClick={() => handleReinstall(device.id, device.type)}
                                                />
                                            ) : (
                                                <img src={Delete} alt="Delete" className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: "pointer" }} title="Delete" onClick={(e) => {
                                                    handleDelete(device.id)
                                                }} />
                                            )
                                        }

                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="text-center p-3">No Device available</td>
                            </tr>
                        )}
                    </tbody>
                </table>



            </div>
            <div className="row justify-content-center mt-4 mb-5">
                <div className="col-auto">
                    <button
                        className="btn btn-sm mx-2"
                        onClick={() => handlePageChange(currentPage - 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                </div>

                <div className="col-auto d-flex align-items-center">
                    <span className="mx-2">Page</span>
                    <input
                        type="number"
                        value={devices.length === 0 ? 0 : inputPage}
                        onChange={handleInputChange}
                        onBlur={handleInputSubmit}
                        onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
                        style={{ width: "60px", textAlign: "center" }}
                        min={1}
                        max={totalPages}
                        disabled={devices.length === 0}
                    />
                    <span className="mx-2">of {totalPages}</span>
                </div>

                <div className="col-auto">
                    <button
                        className="btn btn-sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={currentPage === totalPages || totalPages === 0}
                    >
                        Next
                    </button>
                </div>
            </div>




            {editDevice && (
                <div
                    onClick={() => setEditDevice(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                        padding: "20px",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            borderRadius: "16px",
                            width: "100%",
                            maxWidth: "600px",
                            padding: "24px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                            overflowY: "auto",
                            maxHeight: "90vh",
                            fontFamily: "'Inter', sans-serif",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "16px",
                                marginBottom: "24px",
                            }}
                        >
                            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#333" }}>
                                Edit Device
                            </h2>
                            <button
                                onClick={() => setEditDevice(null)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "22px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    color: "#999",
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Status Section */}
                        <div style={{ marginBottom: "12px" }}>
                            <label
                                style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    marginBottom: "6px",
                                    display: "block",
                                }}
                            >
                                Update Status
                            </label>

                            {status === "removed" ? (
                                <div
                                    style={{
                                        background: "#ffe6e6",
                                        padding: "10px",
                                        borderRadius: "6px",
                                        border: "1px solid #ff4d4f",
                                        color: "#a94442",
                                        fontSize: "13px",
                                    }}
                                >
                                    This device has been removed. You can update the metadata, but to change the status, please reinstall the device at a new location.
                                </div>
                            ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        style={{
                                            flex: 1,
                                            padding: "8px",
                                            fontSize: "13px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                        }}
                                    >
                                        {EditStatusOptions.map((option) => (
                                            <option key={option} value={option.toLowerCase()}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={handleStatusSubmit}
                                        style={{
                                            padding: "8px",
                                            backgroundColor: 'rgb(46, 96, 167)',
                                            border: "none",
                                            borderRadius: "6px",
                                            color: "#fff",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                        title="Save Status"
                                    >
                                        <FiSave size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Metadata Section */}
                        <div
                            style={{
                                padding: "16px",
                                backgroundColor: "#f9f9f9",
                                borderRadius: "10px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                marginBottom: "20px",
                            }}
                        >
                            <label
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    display: "block",
                                    marginBottom: "16px",
                                    color: "#333",
                                }}
                            >
                                Update Metadata
                            </label>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleMetadataSubmit();
                                }}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px 20px",
                                }}
                            >
                                {Object.entries(metadataForm).map(([key, value]) => (
                                    <div key={key} style={{ display: "flex", flexDirection: "column" }}>
                                        <label
                                            style={{
                                                marginBottom: "6px",
                                                fontSize: "13px",
                                                fontWeight: 500,
                                                color: "#555",
                                            }}
                                        >
                                            {key}
                                        </label>
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleMetadataChange(key, e.target.value)}
                                            style={{
                                                padding: "10px",
                                                borderRadius: "8px",
                                                border: "1px solid #ccc",
                                                fontSize: "13px",
                                                color: "#333",
                                            }}
                                        />
                                    </div>
                                ))}

                                <div style={{ gridColumn: "1 / -1", textAlign: "right", marginTop: "8px" }}>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: "10px 20px",
                                            backgroundColor: 'rgb(10, 69, 149)',
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Save Metadata
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}



        </div>
    );
};

export default Device;
